/* eslint-disable @typescript-eslint/no-explicit-any */
import * as amplitude from '@amplitude/analytics-browser';
import { plugin as engagementPlugin } from '@amplitude/engagement-browser';
import { sessionReplayPlugin } from '@amplitude/plugin-session-replay-browser';
import { Experiment } from '@amplitude/experiment-js-client';
import type { ExperimentClient, ExperimentConfig, Variant, Variants } from '@amplitude/experiment-js-client';
import { logger } from '../../helpers/logger';
import { defaultEventPropertiesPlugin } from './defaultEventPropertiesPlugin';
import type { PrescriptionStartedPayload } from './types';
import type { PrescriptionCompletedPayload } from './types';
import type { ConcomitanceViewedPayload } from './types';
import type { ConcomitanceClickedPayload } from './types';
import type { RemovePrescriptionItemClickedPayload } from './types';
import type { EditPrescriptionItemClickedPayload } from './types';
import type { ProtocolTabClickedPayload } from './types';
import type { ProtocolCreatedPayload } from './types';
import type { ProtocolSelectedPayload } from './types';
import type { PopUpViewedPayload } from './types';
import type { PopUpClickedPayload } from './types';
import type { CompoundedMedicationCreatedPayload } from './types';
import type { CompoundedMedicationSavedAsPayload } from './types';
import type { CompoundedMedicationRemovedPayload } from './types';
import type { CompoundedMedicationClickedPayload } from './types';
import type { RenewedFromMedicalRecordPayload } from './types';
import type { ReissueFromMedicalRecordPayload } from './types';
import type { PrescriptionDraftSavedPayload } from './types';
import type { EsignatureSuccessPayload } from './types';
import type { EsignatureManagePayload } from './types';
import type { ManualMedicationClickedPayload } from './types';
import type { PrescriptionSentPayload } from './types';
import type { PatientProfileUpdatedPayload } from './types';
import type { DrugInteractionClickedPayload } from './types';
import type { WhatsNewClickedPayload } from './types';
import type { MevoAppTabClickedPayload } from './types';
import type { MevoAppBannerOpenedPayload } from './types';
import type { MevoAppWhatsappLinkClickedPayload } from './types';
import type { SearchResultClickedPayload } from './types';
import type { MenuClickedPayload } from './types';
import type { BannerClickedPayload } from './types';
import type { PrescriptionCancelledPayload } from './types';
import type { PatientMedicalRecordDetailPayload } from './types';
import type { PrescriptionDraftResumePayload } from './types';
import type { ScheduledRenewalCancelledPayload } from './types';
import type { ScheduledRenewalSignedPayload } from './types';
import type { SampleRequestStartedPayload, SampleRequestCompletedPayload, ExceptionEncounteredPayload } from './types';

export interface SessionReplayOptions {
  /**
   * Taxa de amostragem para gravação de sessões (0.0 a 1.0).
   * Ex: 0.01 grava 1% das sessões, 1.0 grava todas.
   * Default: 0.01
   */
  sampleRate: number;

  /**
   * Configurações de privacidade para mascaramento de dados sensíveis na gravação.
   */
  privacyConfig?: {
    /** Mascara o valor de todos os campos de input. */
    maskAllInputs?: boolean;
    /** Mascara todo o conteúdo de texto visível na tela. */
    maskAllText?: boolean;
    /** Bloqueia (oculta completamente) elementos que correspondam ao seletor CSS. */
    blockSelector?: string;
    /** Mascara elementos que correspondam ao seletor CSS, substituindo o conteúdo por um placeholder. */
    maskSelector?: string;
  };
}

export interface AmplitudeInitOptions {
  /**
   * Habilita o carregamento de configurações remotas definidas no dashboard do Amplitude.
   * Default: true
   */
  fetchRemoteConfig?: boolean;

  /**
   * Habilita a captura automática de eventos pelo SDK.
   * Controlado pela feature flag `Features.enableAmplitudeAutocapture`.
   * Default: false (desligado para evitar custos inesperados)
   */
  autocapture?: boolean;

  /**
   * Configuração do Session Replay para gravação de sessões de usuário.
   */
  sessionReplay?: SessionReplayOptions;

  /**
   * Configuração do Amplitude Experiment (feature flags / A-B testing).
   */
  experiment?: ExperimentOptions;
}

export interface ExperimentOptions {
  /**
   * Deployment key do Amplitude Experiment (encontrado no dashboard).
   * Sem ela o client de experimentos não é inicializado.
   */
  deploymentKey: string;

  /**
   * Configurações avançadas repassadas diretamente ao SDK de experimentos.
   */
  config?: ExperimentConfig;
}

/** Tracking plan metadata. Valores que mudam por projeto ficam em env vars. */
const TRACKING_PLAN_CONFIG: amplitude.Types.BrowserOptions = {
  plan: {
    version: '2',
    branch: 'main',
    source: process.env.REACT_APP_AMPLITUDE_PLAN_SOURCE || '',
    versionId: process.env.REACT_APP_AMPLITUDE_PLAN_VERSION_ID || '',
  },
  ingestionMetadata: {
    sourceName: 'browser-typescript-ampli',
    sourceVersion: '2.0.0',
  },
};

const isDev = process.env.NODE_ENV === 'development';

/**
 * Wrapper sobre o Amplitude Browser SDK.
 * Substitui o ampli-cli gerado, centralizando inicialização, identify,
 * user properties e tracking de eventos.
 *
 * Nenhum evento é emitido e nenhum dado é salvo no localStorage
 * enquanto o módulo não for inicializado (controlado pela feature flag).
 */
class AmplitudeWrapper {
  private initialized = false;
  private client: amplitude.Types.BrowserClient | null = null;
  private experiment: ExperimentClient | null = null;
  private experimentsReady = false;
  private experimentReadyWaiters: Array<() => void> = [];

  isInitialized(): boolean {
    return this.initialized;
  }

  isExperimentInitialized(): boolean {
    return this.experiment !== null;
  }

  /**
   * Inicializa o Amplitude SDK + Session Replay + Guides & Surveys.
   * Deve ser chamado uma única vez na raiz da aplicação.
   */
  init(apiKey: string, options?: AmplitudeInitOptions): void {
    if (this.initialized) return;

    if (!apiKey?.trim()) {
      if (isDev) logger.error('AmplitudeWrapper: API key inválida');
      return;
    }

    try {
      this.client = amplitude.createInstance();

      this.client.add(engagementPlugin());
      this.client.add(defaultEventPropertiesPlugin());

      this.client.init(apiKey, undefined, {
        ...TRACKING_PLAN_CONFIG,
        fetchRemoteConfig: options?.fetchRemoteConfig ?? true,
        autocapture: options?.autocapture ?? false,
      } as any);

      if (options?.sessionReplay) {
        this.initSessionReplay(options.sessionReplay);
      }

      if (options?.experiment?.deploymentKey?.trim()) {
        this.initExperiment(options.experiment);
      }

      this.initialized = true;
    } catch (error) {
      this.initialized = false;
      this.client = null;
      this.experiment = null;
      if (isDev) logger.error('AmplitudeWrapper: Falha na inicialização', error);
    }
  }

  // ── Identify & User Properties ──────────────────────────────────

  /**
   * Identifica o usuário no Amplitude.
   * Seta o user_id na instância do SDK (necessário para segmentação de Guides & Surveys)
   * e dispara um identify event.
   * @param userId ID do usuário.
   */
  async identify(userId: string): Promise<void> {
    if (!this.initialized || !this.client) return;
    try {
      this.client.setUserId(userId);
      const identifyEvent = new amplitude.Identify();
      this.client.identify(identifyEvent);
      await this.fetchExperiments(userId);
    } catch (error) {
      if (isDev) logger.error('AmplitudeWrapper: Falha ao identificar usuário', error);
    }
  }

  whenExperimentsReady(timeoutMs = 3000): Promise<void> {
    if (!this.experiment || this.experimentsReady) return Promise.resolve();

    return new Promise((resolve) => {
      const timer = setTimeout(resolve, timeoutMs);
      this.experimentReadyWaiters.push(() => {
        clearTimeout(timer);
        resolve();
      });
    });
  }

  private markExperimentsReady(): void {
    this.experimentsReady = true;
    this.experimentReadyWaiters.splice(0).forEach((resolve) => resolve());
  }

  /**
   * Seta uma única user property no Amplitude.
   * @param property Nome da propriedade.
   * @param value Valor da propriedade.
   */
  setUserProperty(property: string, value: any): void {
    if (!this.initialized || !this.client) return;
    try {
      const identifyEvent = new amplitude.Identify();
      identifyEvent.set(property, value);
      this.client.identify(identifyEvent);
    } catch (error) {
      if (isDev) logger.error('AmplitudeWrapper: Falha ao setar user property', error);
    }
  }

  /**
   * Seta múltiplas user properties de uma vez.
   * @param properties Objeto chave-valor com as propriedades.
   */
  setUserProperties(properties: Record<string, any>): void {
    if (!this.initialized || !this.client) return;
    try {
      const identifyEvent = new amplitude.Identify();
      for (const [key, value] of Object.entries(properties)) {
        identifyEvent.set(key, value);
      }
      this.client.identify(identifyEvent);
    } catch (error) {
      if (isDev) logger.error('AmplitudeWrapper: Falha ao setar user properties', error);
    }
  }

  // ── Event Tracking ──────────────────────────────────────────────

  /**
   * Trackeia um evento genérico no Amplitude.
   * @param eventName Nome do evento.
   * @param properties Propriedades do evento.
   */
  track(eventName: string, properties?: Record<string, any>): void {
    if (!this.initialized || !this.client) return;
    try {
      this.client.track(eventName, properties);
    } catch (error) {
      if (isDev) logger.error(`AmplitudeWrapper: Falha ao trackear evento "${eventName}"`, error);
    }
  }

  prescriptionStarted(properties: PrescriptionStartedPayload): void {
    this.track('prescription_started', properties);
  }

  prescriptionCompleted(properties: PrescriptionCompletedPayload): void {
    this.track('prescription_completed', properties as any);
  }

  concomitanceViewed(properties: ConcomitanceViewedPayload): void {
    this.track('concomitance_viewed', properties);
  }

  concomitanceClicked(properties: ConcomitanceClickedPayload): void {
    this.track('concomitance_clicked', properties);
  }

  removePrescriptionItemClicked(properties: RemovePrescriptionItemClickedPayload): void {
    this.track('remove_prescription_item_clicked', properties);
  }

  editPrescriptionItemClicked(properties: EditPrescriptionItemClickedPayload): void {
    this.track('edit_prescription_item_clicked', properties);
  }

  protocolTabClicked(properties: ProtocolTabClickedPayload): void {
    this.track('protocol_tab_clicked', properties);
  }

  protocolCreated(properties: ProtocolCreatedPayload): void {
    this.track('protocol_created', properties);
  }

  protocolSelected(properties: ProtocolSelectedPayload): void {
    this.track('protocol_selected', properties);
  }

  popUpViewed(properties: PopUpViewedPayload): void {
    this.track('pop_up_viewed', properties);
  }

  popUpClicked(properties: PopUpClickedPayload): void {
    this.track('pop_up_clicked', properties);
  }

  compoundedMedicationCreated(properties: CompoundedMedicationCreatedPayload): void {
    this.track('compounded_medication_created', properties);
  }

  compoundedMedicationSavedAs(properties: CompoundedMedicationSavedAsPayload): void {
    this.track('compounded_medication_saved_as', properties);
  }

  compoundedMedicationRemoved(properties: CompoundedMedicationRemovedPayload): void {
    this.track('compounded_medication_removed', properties);
  }

  compoundedMedicationClicked(properties: CompoundedMedicationClickedPayload): void {
    this.track('compounded_medication_clicked', properties);
  }

  renewedFromMedicalRecord(properties: RenewedFromMedicalRecordPayload): void {
    this.track('renewed_from_medical_record', properties);
  }

  reissueFromMedicalRecord(properties: ReissueFromMedicalRecordPayload): void {
    this.track('reissue_from_medical_record', properties);
  }

  prescriptionDraftSaved(properties: PrescriptionDraftSavedPayload): void {
    this.track('prescription_draft_saved', properties);
  }

  esignatureSuccess(properties: EsignatureSuccessPayload): void {
    this.track('esignature_sucess', properties);
  }

  esignatureManage(properties: EsignatureManagePayload): void {
    this.track('esignature_manage', properties);
  }

  manualMedicationClicked(properties: ManualMedicationClickedPayload): void {
    this.track('manual_medication_clicked', properties);
  }

  prescriptionSent(properties: PrescriptionSentPayload): void {
    this.track('prescription_sent', properties as any);
  }

  patientProfileUpdated(properties: PatientProfileUpdatedPayload): void {
    this.track('patient_profile_updated', properties as any);
  }

  drugInteractionClicked(properties: DrugInteractionClickedPayload): void {
    this.track('drug_interaction_clicked', properties as any);
  }

  whatsNewClicked(properties: WhatsNewClickedPayload): void {
    this.track('whats_new_clicked', properties as any);
  }

  mevoAppTabClicked(properties: MevoAppTabClickedPayload): void {
    this.track('mevo_app_tab_clicked', properties);
  }

  mevoAppBannerOpened(properties: MevoAppBannerOpenedPayload): void {
    this.track('mevo_app_banner_opened', properties);
  }

  mevoAppWhatsappLinkClicked(properties: MevoAppWhatsappLinkClickedPayload): void {
    this.track('mevo_app_whatsapp_link_clicked', properties);
  }

  menuClicked(properties: Record<string, any>): void {
    this.track('menu_clicked', properties);
  }

  searchResultClicked(properties: SearchResultClickedPayload): void {
    this.track('search_result_clicked', properties);
  }

  bannerClicked(properties: BannerClickedPayload): void {
    this.track('banner_clicked', properties);
  }

  prescriptionCancelled(properties: PrescriptionCancelledPayload): void {
    this.track('prescription_cancelled', properties as any);
  }

  patientMedicalRecordDetail(properties: PatientMedicalRecordDetailPayload): void {
    this.track('patient_medical_record_detail', properties);
  }

  prescriptionDraftResume(properties: PrescriptionDraftResumePayload): void {
    this.track('prescription_draft_resume', properties);
  }

  scheduledRenewalCancelled(properties: ScheduledRenewalCancelledPayload): void {
    this.track('scheduled_renewal_cancelled', properties as any);
  }

  scheduledRenewalSigned(properties: ScheduledRenewalSignedPayload): void {
    this.track('scheduled_renewal_signed', properties as any);
  }

  sampleRequestStarted(properties: SampleRequestStartedPayload): void {
    this.track('sample_request_started', properties as any);
  }

  sampleRequestCompleted(properties: SampleRequestCompletedPayload): void {
    this.track('sample_request_completed', properties as any);
  }

  exceptionEncountered(properties: ExceptionEncounteredPayload): void {
    this.track('exception_encountered', properties as any);
  }

  /**
   * Força o envio imediato dos eventos em buffer.
   */
  flush(): void {
    if (!this.initialized || !this.client) return;
    this.client.flush();
  }

  // ── Session Replay ──────────────────────────────────────────────

  /**
   * Inicializa o plugin de Session Replay para gravação de sessões de usuário.
   * Chamado internamente pelo `init` quando `sessionReplay` é fornecido.
   */
  private initSessionReplay(options: SessionReplayOptions): void {
    if (!this.client) return;
    try {
      const plugin = sessionReplayPlugin({
        sampleRate: options.sampleRate ?? 0.01,
        ...options.privacyConfig,
      });
      this.client.add(plugin as any);
    } catch (error) {
      if (isDev) logger.error('AmplitudeWrapper: Falha ao inicializar Session Replay', error);
    }
  }

  // ── Experiment (Feature Flags / A-B Testing) ────────────────────

  /**
   * Inicializa o Amplitude Experiment conectado à instância de Analytics.
   * Chamado internamente pelo `init` quando `experiment.deploymentKey` é fornecido.
   */
  private initExperiment(options: ExperimentOptions): void {
    try {
      this.experiment = Experiment.initializeWithAmplitudeAnalytics(options.deploymentKey, options.config);
    } catch (error) {
      this.experiment = null;
      if (isDev) logger.error('AmplitudeWrapper: Falha ao inicializar Experiment', error);
    }
  }

  /**
   * Busca (ou re-busca) as variantes de experimento no servidor.
   */
  async fetchExperiments(userId: string): Promise<void> {
    if (!this.experiment) return;
    this.experimentsReady = false;
    try {
      await this.experiment.fetch({ user_id: userId });
    } catch (error) {
      if (isDev) logger.error('AmplitudeWrapper: Falha ao buscar variantes de experimento', error);
    } finally {
      this.markExperimentsReady();
    }
  }

  /**
   * Retorna a variante de uma flag/experimento.
   * @param flagKey Chave da flag configurada no dashboard.
   */
  getVariant(flagKey: string): Variant | undefined {
    if (!this.experiment) return undefined;
    try {
      return this.experiment.variant(flagKey);
    } catch (error) {
      if (isDev) logger.error(`AmplitudeWrapper: Falha ao obter variante "${flagKey}"`, error);
      return undefined;
    }
  }

  /**
   * Retorna apenas o valor (string) da variante de uma flag.
   * @param flagKey Chave da flag configurada no dashboard.
   */
  getVariantValue(flagKey: string): string | undefined {
    return this.getVariant(flagKey)?.value;
  }

  /**
   * Helper booleano: `true` quando a variante da flag é `'on'`.
   * @param flagKey Chave da flag configurada no dashboard.
   */
  isFeatureEnabled(flagKey: string): boolean {
    return this.getVariantValue(flagKey) === 'on';
  }

  /**
   * Retorna todas as variantes atualmente carregadas.
   */
  getAllVariants(): Variants {
    if (!this.experiment) return {};
    try {
      return this.experiment.all();
    } catch (error) {
      if (isDev) logger.error('AmplitudeWrapper: Falha ao obter variantes', error);
      return {};
    }
  }
}

export const amplitudeModule = new AmplitudeWrapper();
